/**
 * Says which kind of "blocked" a failed `getUserMedia` is.
 *
 * A `Permissions-Policy` header that omits the origin and a person tapping
 * "Block" both surface as the same `NotAllowedError`, so the exception alone
 * cannot tell them apart — and guessing "denied" sends someone hunting through
 * browser settings that were never the problem. This site shipped with
 * `camera=()`, which blocks every origin including itself, and that is exactly
 * what happened.
 *
 * The Permissions API resolves it: permission granted *and* the call still
 * refused means the page was never allowed to ask.
 */
export async function describeCameraFailure(error: unknown): Promise<string> {
  if (!(error instanceof DOMException)) return 'The camera could not be started.';

  if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
    return 'No camera was found on this device.';
  }

  if (error.name !== 'NotAllowedError') {
    return `The camera could not be started (${error.name}).`;
  }

  try {
    const status = await navigator.permissions.query({
      name: 'camera' as PermissionName,
    });

    if (status.state === 'granted') {
      return 'This site is blocking camera access, so your browser was never asked. Report this — it is a server setting, not something you can change here.';
    }
  } catch {
    // Firefox and Safari do not expose `camera` to the Permissions API. Fall
    // through to wording that does not assert a cause we cannot establish.
  }

  return 'Camera access was refused. Allow it for this site, or ask IT to check whether the site permits the camera at all.';
}

/** Whether this browser can open a camera at all (a secure context with the API). */
export function cameraAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}
